import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../../components/ConfirmModal';
import { usuarioService } from '../../../services/usuarioService';
import { perfilService } from '../../../services/perfilService';

const initialState = {
  nome: '',
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  perfil_id: '',
  ativo: 1
};

const UsuarioCreate = () => {
  const [form, setForm] = useState(initialState);
  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadPerfis();
  }, []);

  const loadPerfis = async () => {
    try {
      const response = await perfilService.list();
      setPerfis(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar perfis:', err);
    }
  };

  const validate = () => {
    if (!form.nome.trim()) return 'O nome é obrigatório.';
    if (form.nome.length > 255) return 'O nome deve ter até 255 caracteres.';
    
    if (!form.username.trim()) return 'O username é obrigatório.';
    if (form.username.length < 3 || form.username.length > 40) return 'O username deve ter entre 3 e 40 caracteres.';
    if (!/^[a-zA-Z0-9._-]+$/.test(form.username)) return 'O username deve conter apenas letras, números, ponto, hífen e underscore.';
    
    if (!form.email.trim()) return 'O email é obrigatório.';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) return 'Email inválido.';
    
    if (!form.password) return 'A senha é obrigatória.';
    if (form.password.length < 8) return 'A senha deve ter pelo menos 8 caracteres.';
    
    // Validação de complexidade da senha (mesma regra do backend)
    const senhaRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!senhaRegex.test(form.password)) {
      return 'A senha deve ter pelo menos 8 caracteres, uma letra maiúscula, uma minúscula e um número.';
    }
    
    if (form.password !== form.confirmPassword) return 'As senhas não coincidem.';
    
    if (!form.perfil_id) return 'Selecione um perfil.';
    
    return '';
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    try {
      setShowConfirm(false);
      setLoading(true);
      setError('');
      
      const userData = {
        nome: form.nome,
        username: form.username,
        email: form.email,
        password: form.password,
        perfil_id: parseInt(form.perfil_id),
        ativo: form.ativo
      };
      
      await usuarioService.create(userData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/seguranca/usuarios');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Cadastrar Usuário</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Nome *</label>
          <input
            type="text"
            name="nome"
            value={form.nome}
            onChange={handleChange}
            maxLength={255}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Nome completo do usuário"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Username *</label>
          <input
            type="text"
            name="username"
            value={form.username}
            onChange={handleChange}
            maxLength={40}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Username para login (ex: joao.silva)"
          />
          <small className="text-gray-500">Apenas letras, números, ponto, hífen e underscore</small>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Email *</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="email@exemplo.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Senha *</label>
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Senha do usuário"
          />
          <small className="text-gray-500">Mínimo 8 caracteres, com letra maiúscula, minúscula e número</small>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Confirmar Senha *</label>
          <input
            type="password"
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Confirme a senha"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Perfil *</label>
          <select
            name="perfil_id"
            value={form.perfil_id}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">Selecione um perfil</option>
            {perfis.map(perfil => (
              <option key={perfil.id} value={perfil.id}>
                {perfil.nome} - {perfil.descricao}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="ativo"
                value={1}
                checked={form.ativo === 1}
                onChange={(e) => setForm(prev => ({ ...prev, ativo: parseInt(e.target.value) }))}
                className="mr-2"
              />
              Ativo
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="ativo"
                value={0}
                checked={form.ativo === 0}
                onChange={(e) => setForm(prev => ({ ...prev, ativo: parseInt(e.target.value) }))}
                className="mr-2"
              />
              Inativo
            </label>
          </div>
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div className="text-blue-600 text-sm">Salvando...</div>}
        {success && <div className="text-green-600 text-sm">Usuário cadastrado com sucesso!</div>}
        
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button 
            type="button" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" 
            onClick={() => navigate('/seguranca/usuarios')}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button 
            type="submit" 
            className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
           aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>
        </div>
      </form>
      
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Cadastro"
        message="Deseja realmente cadastrar este usuário?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default UsuarioCreate;
